import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { connectionsApi } from '../utils/api';

const stateStyles = {
  pending: 'bg-amber-100 text-amber-700 border-amber-200',
  active: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  severed: 'bg-gray-100 text-gray-600 border-gray-200',
};

export default function ConnectionDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [connection, setConnection] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetch = async () => {
    setLoading(true);
    try {
      const res = await connectionsApi.get(id);
      setConnection(res.data.connection);
    } catch (err) {
      console.error('Failed to fetch connection:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetch(); }, [id]);

  const handleAccept = async () => {
    try {
      await connectionsApi.accept(id);
      await fetch();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to accept');
    }
  };

  const handleSever = async () => {
    if (!window.confirm('Sever this connection?')) return;
    try {
      await connectionsApi.sever(id);
      await fetch();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to sever');
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-32">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
        </div>
      </Layout>
    );
  }
  if (!connection) {
    return (
      <Layout>
        <p className="text-gray-600">Connection not found.</p>
      </Layout>
    );
  }

  const counterparty = connection.myRole === 'seller' ? connection.buyerWorkspace : connection.sellerWorkspace;
  const canAccept =
    connection.state === 'pending' && connection.initiatorWorkspaceId !== counterparty.id;

  return (
    <Layout>
      <div className="max-w-4xl mx-auto">
        <Link to="/connections" className="text-sm text-gray-600 hover:text-gray-700 mb-2 inline-block">
          &larr; Back to Connections
        </Link>

        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mt-2 mb-8">
          <div>
            <h1 className="page-header">{counterparty.name}</h1>
            <p className="text-gray-600 mt-1">
              {connection.myRole === 'seller' ? 'You sell to this workspace' : 'You buy from this workspace'}
            </p>
          </div>
          <div className="flex items-center gap-3 mt-4 sm:mt-0">
            <span className={`inline-block px-2 py-0.5 text-xs font-medium rounded-full border ${stateStyles[connection.state]}`}>
              {connection.state}
            </span>
            {canAccept && (
              <button onClick={handleAccept} className="btn-primary text-sm">Accept</button>
            )}
            {connection.state !== 'severed' && (
              <button onClick={handleSever} className="btn-secondary text-sm">Sever</button>
            )}
          </div>
        </div>

        {/* Counterparty card */}
        <div className="card p-6 mb-6">
          <h2 className="text-sm font-medium text-gray-600 uppercase mb-4">Counterparty</h2>
          <div className="space-y-3">
            <div>
              <p className="text-xs text-gray-600">Workspace</p>
              <p className="text-sm font-medium text-gray-900">{counterparty.name}</p>
              <p className="text-xs text-gray-600">{counterparty.slug}</p>
            </div>
            {counterparty.metadata?.migrated_from && (
              <div>
                <p className="text-xs text-gray-600">Origin</p>
                <p className="text-sm text-gray-700">
                  Migrated from v1 <span className="text-gray-600">({counterparty.metadata.migrated_from})</span>
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Activity */}
        <div className="card p-6 mb-6">
          <h2 className="text-sm font-medium text-gray-600 uppercase mb-4">Activity</h2>
          <ul className="space-y-2 text-sm text-gray-600">
            <li>Initiated {new Date(connection.initiatedAt).toLocaleDateString()}</li>
            {connection.acceptedAt && (
              <li>Accepted {new Date(connection.acceptedAt).toLocaleDateString()}</li>
            )}
            {connection.severedAt && (
              <li>Severed {new Date(connection.severedAt).toLocaleDateString()}</li>
            )}
          </ul>
        </div>

        {/* Proposals + Agreements placeholder */}
        <div className="card p-6">
          <h2 className="text-sm font-medium text-gray-600 uppercase mb-2">Proposals &amp; Agreements</h2>
          <p className="text-sm text-gray-600">
            Proposals and Agreements inside this Connection will surface here once the Proposal editor
            is reparented to the Connection scope (in a later PR). For now, manage them from the
            top-level Proposals and Agreements pages.
          </p>
        </div>
      </div>
    </Layout>
  );
}
